import Collapse from '@kunukn/react-collapse';
import loadable from '@loadable/component';
import { motion } from 'framer-motion';
import {
    ChevronDown,
    Clock,
    History as HistoryIcon,
    LayoutDashboard,
    Menu,
    Search,
    Settings,
    Wallet,
    X,
} from 'lucide-react';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link, NavLink } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import styled from 'styled-components';
import { actions } from '../actions';
import nftDetails from '../config/nftDetails';
import { getContractInstance } from '../helper/functions';
import {
    forceBSCTestnetWeb3,
    refreshWeb3Instance,
    walletConnectProvider,
    web3,
} from '../web3';
import { CommandPalette } from './CommandPalette';
import { AvBTN02, DDBtnbar02, DesktopLogin } from './header-style';
import { ThemeToggle } from './ThemeToggle';
import { VestnLogo } from './VestnLogo';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Small circle loader component
const CircleLoader = styled.div`
    width: 16px;
    height: 16px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #bae325;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
    margin-right: 8px;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const ThirdWebLogin = loadable(() => import('./Modals/thirdWebLogin'));
const BecomeCreator = loadable(() => import('./Modals/become-creator'));
const NotificationDropdown = loadable(() => import('./NotificationDropdown'));

const ProfileLink = styled.div`
    color: hsl(var(--foreground));
    button:hover & {
        color: #1e293b;
    }
`;

interface NotificationItem {
    notificationStatus: string;
    // Add other notification properties as needed
}

interface HeaderProps extends RouteComponentProps {
    web3Data: {
        isLoggedIn: boolean;
        accounts: string[];
    };
    networkId: number;
    isMetamaskEnabled: boolean;
    nonce: string | null;
    authData: {
        data: UserDetails | null;
    } | null;
    profileData: {
        profile?: string;
    } | null;
    notifications: any;
    getWeb3: () => void;
    enableMetamask: () => void;
    generateNonce: (address: string) => void;
    clearNonce: () => void;
    authLogin: (nonce: string, signature: string) => void;
    authenticateUser: () => void;
    getUserDetails: () => void;
    authLogout: () => void;
    resetUserSession: () => void;
    resetUserSessionForWalletSwitch: (accounts: string[]) => void;
    getReadNotifications: () => void;
    getNotifications: () => void;
    web3Logout: (accounts: string[]) => void;
}

interface Role {
    roleName: string;
}

interface UserDetails {
    role: Role;
    status: string;
    profile?: string;
}

interface HeaderState {
    isOpen1: boolean;
    isOpen2: boolean;
    isOpen3: boolean;
    isOpen4: boolean;
    isOpen11: boolean;
    isOpen12: boolean;
    web3Data: {
        isLoggedIn: boolean;
        accounts: string[];
    };
    loader: boolean;
    error: { isError: boolean; msg: string };
    userDetails: UserDetails | null;
    accountBalance: number;
    compactUserAddress: string;
    hasUnreadNotifications: boolean;
    unreadNotificationCount: number;
    isLoadingBalance: boolean;
    currentNetworkId: number | null;
    scrolled: boolean;
    isDarkMode: boolean;
}

interface Web3State {
    fetchWeb3Data: {
        isLoggedIn: boolean;
        accounts: string[];
    };
    fetchNetworkId: number;
    fetchMetamask: boolean;
    fetchNonce: string | null;
    fetchAuthData: {
        data: UserDetails | null;
    } | null;
    fetchProfile: {
        profile?: string;
    } | null;
    fetchNotifications: {
        data: Notification[] | null;
    } | null;
}

class Header extends Component<HeaderProps, HeaderState> {
    wrapperRef: React.RefObject<HTMLDivElement | null>;
    lastUserDetailsAccount: string | null = null;

    formatCompactAddress = (address?: string | null): string => {
        if (!address) return '';
        if (address.length <= 12) return address;
        return `${address.slice(0, 7)}...${address.slice(-5)}`;
    };

    constructor(props: HeaderProps) {
        super(props);
        this.wrapperRef = React.createRef<HTMLDivElement>();
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.state = {
            isOpen1: false,
            isOpen2: false,
            isOpen3: false,
            isOpen4: false,
            isOpen11: false,
            isOpen12: false,
            web3Data: {
                isLoggedIn: false,
                accounts: [],
            },
            loader: false,
            error: { isError: false, msg: '' },
            userDetails: null,
            accountBalance: 0,
            compactUserAddress: '',
            hasUnreadNotifications: false,
            unreadNotificationCount: 0,
            isLoadingBalance: false,
            currentNetworkId: null,
            scrolled: false,
            isDarkMode: true,
        };
    }

    static getDerivedStateFromProps(
        nextProps: HeaderProps,
        prevState: HeaderState,
    ): Partial<HeaderState> | null {
        const { web3Data } = nextProps;
        if (web3Data !== prevState.web3Data) return { web3Data: web3Data };
        return null;
    }

    async componentDidUpdate(
        prevProps: HeaderProps,
        prevState: HeaderState,
    ): Promise<void> {
        const { web3Data, authData } = this.props;

        if (prevState.isOpen11 !== this.state.isOpen11) {
            document.body.style.overflow = this.state.isOpen11 ? 'hidden' : '';
        }

        // Close mobile sidebar on route change
        if (prevProps.location.pathname !== this.props.location.pathname) {
            if (this.state.isOpen11) {
                this.setState({ isOpen11: false });
            }
        }

        // Check if disconnect is in progress to prevent state updates during disconnect
        const disconnectInProgress = sessionStorage.getItem(
            'disconnectInProgress',
        );
        if (disconnectInProgress === 'true') {
            return;
        }

        // Check if we need to restore state from localStorage
        const userAddress = localStorage.getItem('userAddress');
        const rwaAuthToken = localStorage.getItem('RwaAuthToken');
        const usingThirdweb = localStorage.getItem('usingThirdweb');

        // If we have userAddress and token but no accounts in state, restore the state
        if (
            userAddress &&
            rwaAuthToken &&
            usingThirdweb &&
            !web3Data.accounts[0] &&
            web3Data.isLoggedIn
        ) {
            // Use a flag to prevent infinite loops in componentDidUpdate
            const stateRestoreAttempted = sessionStorage.getItem(
                'stateRestoreAttempted',
            );
            if (!stateRestoreAttempted) {
                sessionStorage.setItem('stateRestoreAttempted', 'true');
                this.restoreUserState();
                return;
            }
        }

        if (web3Data.accounts[0] !== prevProps.web3Data.accounts[0]) {
            // Check if user is using thirdweb

            // Only clear userAddress if the new account is empty (user disconnected) and not using thirdweb
            // Don't clear if we're getting a new account (user connected) or if using thirdweb
            if (!web3Data.accounts[0] && !usingThirdweb) {
                localStorage.setItem('userAddress', '');
            } else if (web3Data.accounts[0]) {
                // Update localStorage with the new account address
                localStorage.setItem('userAddress', web3Data.accounts[0]);

                // Refetch only when the wallet account actually changes, not when
                // getUserDetails hydrates web3 state on initial load.
                if (
                    rwaAuthToken &&
                    web3Data.accounts[0] !== this.lastUserDetailsAccount
                ) {
                    this.lastUserDetailsAccount = web3Data.accounts[0];
                    this.props.getUserDetails();
                }
            }
            // For thirdweb users with empty accounts but valid token, don't clear userAddress
            // This prevents the address from being cleared during authentication

            this.setState({ web3Data: web3Data }, () => {
                if (web3Data.accounts[0]) {
                    // Immediately update the compact address display
                    const newAddress = web3Data.accounts[0];
                    const compactUserAddress =
                        this.formatCompactAddress(newAddress);

                    this.setState({
                        compactUserAddress,
                        isLoadingBalance: true,
                    });

                    // Check metamask lock and fetch token balance
                    this.checkMetamaskLock();
                    this.fetchTokenBalance(web3Data);
                }
            });
        }

        // Also handle isLoggedIn changes separately to ensure state updates
        if (web3Data.isLoggedIn !== prevProps.web3Data.isLoggedIn) {
            this.setState({ web3Data: web3Data });

            // If user just logged in and has accounts, update the display immediately
            if (web3Data.isLoggedIn && web3Data.accounts[0]) {
                const newAddress = web3Data.accounts[0];
                const compactUserAddress =
                    this.formatCompactAddress(newAddress);

                this.setState({ compactUserAddress, isLoadingBalance: true });
                this.fetchTokenBalance(web3Data);
            }
        }

        if (authData !== prevProps.authData) {
            if (authData) {
                this.setState({ userDetails: authData.data });
            }
        }
        if (prevProps.notifications !== this.props.notifications) {
            this.checkUnreadStatus();
        }
    }

    checkMetamaskLock = (): void => {
        const checker = setInterval(() => {
            // Check if authentication is in progress to prevent disconnect during login
            const authInProgress = localStorage.getItem('authInProgress');
            const usingThirdweb = localStorage.getItem('usingThirdweb');
            const disconnectInProgress = sessionStorage.getItem(
                'disconnectInProgress',
            );

            // Skip metamask lock check for thirdweb users or if disconnect is in progress
            if (
                authInProgress === 'true' ||
                usingThirdweb === 'true' ||
                disconnectInProgress === 'true'
            ) {
                clearInterval(checker);
                return;
            }

            web3?.eth
                .getAccounts()
                .then((resp: string[]) => {
                    if (!resp[0]) {
                        this.disconnect(); // metaMask is locked disconnect the user
                        clearInterval(checker);
                    }
                })
                .catch((error: unknown) => {
                    console.error('Error checking MetaMask accounts:', error);
                    // Don't disconnect on error, just clear the interval
                    clearInterval(checker);
                });
        }, 1000);
    };

    checkUnreadStatus = () => {
        const { notifications } = this.props;
        if (!notifications || !Array.isArray(notifications)) {
            this.setState({
                hasUnreadNotifications: false,
                unreadNotificationCount: 0,
            });
            return;
        }
        const unreadCount = notifications.filter(
            (notification: NotificationItem) =>
                notification.notificationStatus === 'unread',
        ).length;
        this.setState({
            hasUnreadNotifications: unreadCount > 0,
            unreadNotificationCount: unreadCount,
        });
    };

    handleNotificationClick = async () => {
        this.toggle(3);
        if (this.state.hasUnreadNotifications) {
            await this.props.getReadNotifications();
            await this.props.getNotifications(); // Refresh notifications after marking as read
            this.setState({
                hasUnreadNotifications: false,
                unreadNotificationCount: 0,
            });
        }
    };

    // Method to restore user state from localStorage
    restoreUserState = (): void => {
        const userAddress = localStorage.getItem('userAddress');
        const rwaAuthToken = localStorage.getItem('RwaAuthToken');
        const usingThirdweb = localStorage.getItem('usingThirdweb');

        if (userAddress && rwaAuthToken && usingThirdweb) {
            // Use a flag to prevent multiple restorations
            const userStateRestored =
                sessionStorage.getItem('userStateRestored');
            if (userStateRestored) {
                return;
            }

            sessionStorage.setItem('userStateRestored', 'true');

            const compactUserAddress = this.formatCompactAddress(userAddress);

            const web3Data = {
                isLoggedIn: true,
                accounts: [userAddress],
            };

            this.setState({
                compactUserAddress,
                web3Data,
                isLoadingBalance: true,
            });

            // Try to fetch token balance
            this.fetchTokenBalance(web3Data);

            // For Coinbase Wallet, also try to restore the actual connection
            if (
                typeof window.ethereum !== 'undefined' &&
                window.ethereum.isCoinbaseWallet
            ) {
                this.restoreCoinbaseConnection();
            }
        }
    };

    // Method to restore Coinbase Wallet connection
    restoreCoinbaseConnection = async (): Promise<void> => {
        try {
            if (
                typeof window.ethereum !== 'undefined' &&
                window.ethereum.isCoinbaseWallet
            ) {
                const accounts = (await window.ethereum.request({
                    method: 'eth_accounts',
                })) as string[];

                if (accounts && accounts.length > 0) {
                    // Update the web3 data with the actual connected account
                    const web3Data = {
                        isLoggedIn: true,
                        accounts: accounts,
                    };

                    // Only update state if the accounts are different to prevent infinite loops
                    if (this.state.web3Data.accounts[0] !== accounts[0]) {
                        this.setState({ web3Data }, () => {
                            this.fetchTokenBalance(web3Data);
                        });
                    }
                }
            }
        } catch (error) {
            console.error(
                '❌ Error restoring Coinbase Wallet connection:',
                error,
            );
        }
    };

    componentWillUnmount(): void {
        document.removeEventListener('mousedown', this.handleClickOutside);
        window.removeEventListener('scroll', this.handleScroll);
        document.body.style.overflow = '';
        // Clean up session storage
        sessionStorage.removeItem('coinbaseRestoreAttempted');
        sessionStorage.removeItem('stateRestoreAttempted');
        sessionStorage.removeItem('userStateRestored');
        sessionStorage.removeItem('disconnectInProgress');
    }

    async componentDidMount(): Promise<void> {
        document.addEventListener('mousedown', this.handleClickOutside);
        window.addEventListener('scroll', this.handleScroll);

        // Clear any previous session flags to allow fresh attempts
        sessionStorage.removeItem('coinbaseRestoreAttempted');
        sessionStorage.removeItem('stateRestoreAttempted');
        sessionStorage.removeItem('userStateRestored');
        sessionStorage.removeItem('disconnectInProgress');

        const { web3Data } = this.props;

        const userAddress = localStorage.getItem('userAddress');
        const rwaAuthToken = localStorage.getItem('RwaAuthToken');
        const usingThirdweb = localStorage.getItem('usingThirdweb');

        // Check if user is already logged in
        if (userAddress && rwaAuthToken) {
            this.lastUserDetailsAccount = userAddress;
            this.props.getUserDetails();

            // Force update the display if we have userAddress but no accounts in state
            if (!web3Data.accounts[0] && usingThirdweb) {
                this.restoreUserState();

                // For Coinbase Wallet users, try to restore connection without setTimeout
                if (
                    typeof window.ethereum !== 'undefined' &&
                    window.ethereum.isCoinbaseWallet
                ) {
                    // Use a flag to prevent infinite loops
                    const coinbaseRestoreAttempted = sessionStorage.getItem(
                        'coinbaseRestoreAttempted',
                    );
                    if (!coinbaseRestoreAttempted) {
                        sessionStorage.setItem(
                            'coinbaseRestoreAttempted',
                            'true',
                        );
                        this.restoreCoinbaseConnection();
                    }
                }
            }
        }

        // Only call getWeb3 if no accounts are available and user is not using thirdweb
        if (!web3Data.accounts[0] && !usingThirdweb) {
            this.props.getWeb3();
        } else if (web3Data.accounts[0]) {
            this.setState(
                { web3Data: web3Data, isLoadingBalance: true },
                () => {
                    if (web3Data.accounts[0]) {
                        this.fetchTokenBalance(web3Data);
                    }
                },
            );
        }

        // Network switching logic - run for all wallet types
        if (window.ethereum) {
            try {
                const chainID = await web3?.eth.getChainId();

                // Fix the type comparison - convert both to same type
                if (chainID !== 97 && chainID?.toString() !== '0x61') {
                    // Check if the network is already added to prevent conflicts
                    try {
                        await window.ethereum.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{ chainId: '0x61' }],
                        });
                    } catch (switchError: any) {
                        // This error code indicates that the chain has not been added to MetaMask
                        if (switchError.code === 4902) {
                            try {
                                await window.ethereum.request({
                                    method: 'wallet_addEthereumChain',
                                    params: [
                                        {
                                            chainId: '0x61', //BSC Testnet - correct chain ID
                                            chainName:
                                                'Binance Smart Chain Testnet', //BSC Testnet
                                            nativeCurrency: {
                                                name: 'tBNB',
                                                symbol: 'tBNB',
                                                decimals: 18,
                                            },
                                            rpcUrls: [
                                                'https://data-seed-prebsc-1-s1.binance.org:8545/',
                                            ],
                                            blockExplorerUrls: [
                                                'https://testnet.bscscan.com/',
                                            ],
                                        },
                                    ],
                                });
                            } catch (addError) {
                                console.error(
                                    '❌ Failed to add BSC Testnet on mount:',
                                    addError,
                                );
                            }
                        }
                    }
                } else {
                }

                // Update the current network ID
                await this.updateCurrentNetworkId();
            } catch (error) {
                console.warn('Network configuration error:', error);
            }
        }

        // Use correct type for ethereum event handling
        if (window.ethereum && 'on' in window.ethereum) {
            (window.ethereum as any).on(
                'accountsChanged',
                (accounts: string[]) => {
                    // Check if authentication is in progress to prevent logout during login
                    const authInProgress =
                        localStorage.getItem('authInProgress');
                    if (authInProgress === 'true') {
                        return;
                    }

                    // metamask user address changed
                    if (
                        accounts.length > 0 &&
                        this.state.web3Data.isLoggedIn &&
                        accounts[0] !== this.state.web3Data.accounts[0]
                    ) {
                        this.props.clearNonce();
                        this.props.resetUserSessionForWalletSwitch(accounts);
                        this.setState({ isOpen4: true });
                    }
                },
            );

            (window.ethereum as any).on(
                'chainChanged',
                async (chainId: string) => {
                    // Check if we're currently switching networks to prevent reload
                    const isSwitchingNetwork =
                        localStorage.getItem('switchingNetwork');

                    if (isSwitchingNetwork === 'true') {
                        return;
                    }

                    // Refresh the web3 instance to get the updated network
                    await this.refreshWeb3Connection();

                    // Update the current network ID
                    const chainIdNumber = parseInt(chainId, 16);
                    this.setState({ currentNetworkId: chainIdNumber });

                    // Only reload if switching to an unsupported network
                    // Don't reload when switching to BSC Testnet (0x61) as it's our target network
                    if (chainId !== '0x61' && chainId !== '0x38') {
                        // Show warning for unsupported networks
                        // You can add a notification here if needed
                    }
                    // Remove the automatic reload to prevent session loss
                    // window.location.reload();
                },
            );
        }

        if (walletConnectProvider && 'on' in walletConnectProvider) {
            walletConnectProvider.on(
                'accountsChanged',
                (accounts: string[]) => {
                    // Check if authentication is in progress to prevent logout during login
                    const authInProgress =
                        localStorage.getItem('authInProgress');
                    if (authInProgress === 'true') {
                        return;
                    }

                    // walletConnect user address changed
                    if (
                        accounts.length > 0 &&
                        this.state.web3Data.isLoggedIn &&
                        accounts[0] !== this.state.web3Data.accounts[0]
                    ) {
                        this.props.clearNonce();
                        this.props.resetUserSessionForWalletSwitch(accounts);
                        this.setState({ isOpen4: true });
                    }
                },
            );
        }
    }

    handleClickOutside(event: MouseEvent): void {
        if (
            this.wrapperRef &&
            this.wrapperRef.current &&
            !this.wrapperRef.current.contains(event.target as Node)
        ) {
            if (this.state.isOpen3) {
                this.setState({ isOpen3: false });
            }
            if (this.state.isOpen2) {
                this.setState({ isOpen2: false });
            }
            // Close mobile sidebar when clicking outside
            if (this.state.isOpen11) {
                this.setState({ isOpen11: false });
            }
        }
    }

    // Method to ensure web3 is properly initialized
    ensureWeb3Initialized = async (): Promise<boolean> => {
        let attempts = 0;
        const maxAttempts = 10;

        while (attempts < maxAttempts) {
            if (web3 && web3.eth) {
                return true;
            }

            await new Promise((resolve) => setTimeout(resolve, 500));
            attempts++;
        }

        console.warn('⚠️ Web3 initialization timeout');
        return false;
    };

    // Method to switch to BSC Testnet
    switchToBSCTestnet = async (): Promise<boolean> => {
        if (!window.ethereum) {
            console.warn('⚠️ No ethereum provider available');
            return false;
        }

        try {
            // First try to switch to the network
            try {
                await window.ethereum.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: '0x61' }],
                });

                // Wait for the switch to complete and web3 to update

                await new Promise((resolve) => setTimeout(resolve, 3000));

                // Refresh the web3 instance to get the updated network
                await this.refreshWeb3Connection();

                // Verify the switch worked by checking the chain ID
                try {
                    const newChainId = await web3?.eth.getChainId();

                    if (newChainId === 97) {
                        this.setState({ currentNetworkId: 97 });
                        return true;
                    } else {
                        console.warn(
                            '⚠️ Switch may not have completed, current chain ID:',
                            newChainId,
                        );
                        // Try refreshing one more time
                        await this.refreshWeb3Connection();
                        const finalChainId = await web3?.eth.getChainId();

                        if (finalChainId === 97) {
                            this.setState({ currentNetworkId: 97 });
                            return true;
                        } else {
                            // Still update the state as the switch was requested
                            this.setState({ currentNetworkId: 97 });
                            return true;
                        }
                    }
                } catch (chainError) {
                    // Still update the state as the switch was requested
                    this.setState({ currentNetworkId: 97 });
                    return true;
                }
            } catch (switchError: any) {
                // If the chain is not added, try to add it
                if (switchError.code === 4902) {
                    try {
                        await window.ethereum.request({
                            method: 'wallet_addEthereumChain',
                            params: [
                                {
                                    chainId: '0x61',
                                    chainName: 'Binance Smart Chain Testnet',
                                    nativeCurrency: {
                                        name: 'tBNB',
                                        symbol: 'tBNB',
                                        decimals: 18,
                                    },
                                    rpcUrls: [
                                        'https://data-seed-prebsc-1-s1.binance.org:8545/',
                                    ],
                                    blockExplorerUrls: [
                                        'https://testnet.bscscan.com/',
                                    ],
                                },
                            ],
                        });

                        // Wait for the addition to complete

                        await new Promise((resolve) =>
                            setTimeout(resolve, 3000),
                        );

                        // Verify the addition worked
                        try {
                            const newChainId = await web3?.eth.getChainId();

                            if (newChainId === 97) {
                                this.setState({ currentNetworkId: 97 });
                                return true;
                            } else {
                                console.warn(
                                    '⚠️ Addition may not have completed, current chain ID:',
                                    newChainId,
                                );
                                this.setState({ currentNetworkId: 97 });
                                return true;
                            }
                        } catch (chainError) {
                            console.warn(
                                '⚠️ Error verifying chain ID after adding network:',
                                chainError,
                            );
                            this.setState({ currentNetworkId: 97 });
                            return true;
                        }
                    } catch (addError) {
                        console.error(
                            '❌ Failed to add BSC Testnet:',
                            addError,
                        );
                        return false;
                    }
                }

                console.error(
                    '❌ Failed to switch to BSC Testnet:',
                    switchError,
                );
                return false;
            }
        } catch (error) {
            console.error('❌ Network switch error:', error);
            return false;
        }
    };

    // Method to update current network ID
    updateCurrentNetworkId = async (): Promise<void> => {
        try {
            if (web3 && web3.eth) {
                const chainId = await web3.eth.getChainId();
                this.setState({ currentNetworkId: chainId });
            }
        } catch (error) {
            console.warn('⚠️ Failed to update current network ID:', error);
        }
    };

    // Method to refresh web3 connection
    refreshWeb3Connection = async (): Promise<void> => {
        try {
            // Use the global refresh function to create a new web3 instance
            refreshWeb3Instance();

            // Wait a moment for the new instance to be ready
            await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
            console.warn('⚠️ Error refreshing web3 connection:', error);
        }
    };

    // Method to force web3 to BSC Testnet
    forceBSCTestnetConnection = async (): Promise<void> => {
        try {
            // Use the global force function to create a new web3 instance with BSC Testnet
            forceBSCTestnetWeb3();

            // Wait a moment for the new instance to be ready
            await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
            console.warn(
                '⚠️ Error forcing web3 connection to BSC Testnet:',
                error,
            );
        }
    };

    async fetchTokenBalance(web3Data: {
        isLoggedIn: boolean;
        accounts: string[];
    }): Promise<void> {
        // Set loading state
        this.setState({ isLoadingBalance: true });

        const maxRetries = 3;
        let retryCount = 0;
        let networkSwitchAttempted = false;

        const attemptFetch = async (): Promise<void> => {
            try {
                // Ensure web3 is properly initialized
                const web3Ready = await this.ensureWeb3Initialized();
                if (!web3Ready) {
                    throw new Error('Web3 not initialized');
                }

                // Validate account address
                if (
                    !web3Data.accounts[0] ||
                    !web3?.utils.isAddress(web3Data.accounts[0])
                ) {
                    throw new Error('Invalid account address');
                }

                // Check if we're on the correct network
                let currentChainId = await web3?.eth.getChainId();

                if (currentChainId !== 97 && !networkSwitchAttempted) {
                    console.warn(
                        '⚠️ Not on BSC Testnet (chainId: 97), current:',
                        currentChainId,
                    );

                    // Try to switch to BSC Testnet if not already on it
                    if (window.ethereum) {
                        networkSwitchAttempted = true;
                        try {
                            await window.ethereum.request({
                                method: 'wallet_switchEthereumChain',
                                params: [{ chainId: '0x61' }],
                            });

                            // Wait longer for the switch to complete and web3 to update

                            await new Promise((resolve) =>
                                setTimeout(resolve, 3000),
                            );

                            // Refresh the web3 instance to get the updated network
                            await this.refreshWeb3Connection();

                            // Force refresh web3 connection by getting a new chain ID
                            try {
                                currentChainId = await web3?.eth.getChainId();

                                // If still not on the correct network, wait a bit more and refresh again
                                if (currentChainId !== 97) {
                                    await new Promise((resolve) =>
                                        setTimeout(resolve, 2000),
                                    );
                                    await this.refreshWeb3Connection();
                                    currentChainId =
                                        await web3?.eth.getChainId();
                                }
                            } catch (chainError) {
                                console.warn(
                                    '⚠️ Error getting chain ID after switch:',
                                    chainError,
                                );
                            }

                            // Only retry if we're now on the correct network
                            if (currentChainId === 97) {
                                return attemptFetch(); // Retry the fetch after network switch
                            } else {
                                console.warn(
                                    '⚠️ Still not on BSC Testnet after switch, trying to force connection...',
                                );

                                // As a last resort, try to force the web3 connection to BSC Testnet
                                try {
                                    await this.forceBSCTestnetConnection();
                                    const forcedChainId =
                                        await web3?.eth.getChainId();

                                    if (forcedChainId === 97) {
                                        return attemptFetch();
                                    } else {
                                        console.warn(
                                            '⚠️ Failed to force BSC Testnet, continuing with current network',
                                        );
                                    }
                                } catch (forceError) {
                                    console.warn(
                                        '⚠️ Error forcing BSC Testnet connection:',
                                        forceError,
                                    );
                                }

                                // Don't retry again to prevent infinite loops
                            }
                        } catch (switchError: any) {
                            console.warn(
                                '⚠️ Failed to switch network:',
                                switchError,
                            );

                            // If the chain is not added, try to add it
                            if (switchError.code === 4902) {
                                try {
                                    await window.ethereum.request({
                                        method: 'wallet_addEthereumChain',
                                        params: [
                                            {
                                                chainId: '0x61',
                                                chainName:
                                                    'Binance Smart Chain Testnet',
                                                nativeCurrency: {
                                                    name: 'tBNB',
                                                    symbol: 'tBNB',
                                                    decimals: 18,
                                                },
                                                rpcUrls: [
                                                    'https://data-seed-prebsc-1-s1.binance.org:8545/',
                                                ],
                                                blockExplorerUrls: [
                                                    'https://testnet.bscscan.com/',
                                                ],
                                            },
                                        ],
                                    });

                                    // Wait longer for the addition to complete

                                    await new Promise((resolve) =>
                                        setTimeout(resolve, 3000),
                                    );

                                    // Check if we're now on the correct network
                                    currentChainId =
                                        await web3?.eth.getChainId();

                                    if (currentChainId === 97) {
                                        return attemptFetch(); // Retry the fetch after adding network
                                    } else {
                                        console.warn(
                                            '⚠️ Still not on BSC Testnet after adding, continuing with current network',
                                        );
                                        // Don't retry again to prevent infinite loops
                                    }
                                } catch (addError) {
                                    console.error(
                                        '❌ Failed to add BSC Testnet:',
                                        addError,
                                    );
                                }
                            }
                        }
                    }
                }

                const tokenContractInstance = getContractInstance(
                    undefined,
                    'token',
                );
                if (!tokenContractInstance) {
                    console.warn(
                        '⚠️ Token contract instance not available, using fallback',
                    );
                    // Use fallback - show 0 balance but still display the address
                    const newAddress = web3Data.accounts[0];
                    const compactUserAddress =
                        this.formatCompactAddress(newAddress);

                    this.setState({
                        accountBalance: 0,
                        compactUserAddress,
                        isLoadingBalance: false,
                    });

                    return;
                }

                // Debug: Log contract details
                // Add proper gas estimation and error handling
                let balanceWei;
                try {
                    balanceWei = await tokenContractInstance.methods
                        .balanceOf(web3Data.accounts[0])
                        .call({
                            from: web3Data.accounts[0],
                            gas: 100000, // Set explicit gas limit
                        });
                } catch (contractError) {
                    console.warn(
                        '⚠️ Contract call failed, trying without gas limit:',
                        contractError,
                    );
                    // Try without explicit gas limit
                    balanceWei = await tokenContractInstance.methods
                        .balanceOf(web3Data.accounts[0])
                        .call();
                }

                // Convert balance to number and handle potential null/undefined
                const balanceNumber =
                    web3 && balanceWei
                        ? Number(
                            Number(
                                web3.utils.fromWei(
                                    balanceWei.toString(),
                                    'ether',
                                ),
                            ).toFixed(2),
                        )
                        : 0;

                // Format the address
                const newAddress = web3Data.accounts[0];
                const compactUserAddress =
                    this.formatCompactAddress(newAddress);

                // Set state with the numeric balance value and stop loading
                this.setState({
                    accountBalance: balanceNumber,
                    compactUserAddress,
                    isLoadingBalance: false,
                });

                return; // Success, exit the retry loop
            } catch (error: any) {
                console.error(
                    `❌ Error fetching token balance (attempt ${retryCount + 1
                    }):`,
                    error,
                );

                retryCount++;

                if (retryCount < maxRetries && !networkSwitchAttempted) {
                    // Wait longer between retries, but only if we haven't attempted network switching
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    return attemptFetch();
                } else {
                    console.error(
                        '❌ Max retries reached, setting default values',
                    );

                    // Set default values on error, but still show the address
                    const newAddress = web3Data.accounts[0];
                    const compactUserAddress =
                        this.formatCompactAddress(newAddress);

                    this.setState({
                        accountBalance: 0,
                        compactUserAddress,
                        isLoadingBalance: false,
                    });
                }
            }
        };

        await attemptFetch();
    }

    checkRole = (user: UserDetails | null): JSX.Element => {
        if (!user) return <></>;
        if (user.role.roleName === 'USER') {
            return <BecomeCreator />;
        } else if (
            user.role.roleName === 'CREATOR' &&
            user.status === 'APPROVED'
        ) {
            return (
                <Link to='/user/nftminting'>
                    <AvBTN02 className='colorBTN'>
                        <FormattedMessage id='create' defaultMessage='Create' />
                    </AvBTN02>
                </Link>
            );
        } else if (
            user.role.roleName === 'CREATOR' &&
            user.status !== 'APPROVED'
        ) {
            return (
                <AvBTN02 className='grayBTN'>
                    <FormattedMessage id='waitlist' defaultMessage='Waitlist' />
                </AvBTN02>
            );
        }
        // This should never happen, but TypeScript requires a return for all code paths
        return <></>;
    };

    disconnect = (): void => {
        // Check if authentication is in progress to prevent reload during login
        const authInProgress = localStorage.getItem('authInProgress');
        if (authInProgress === 'true') {
            return;
        }

        // Check if disconnect is already in progress to prevent infinite loops
        const disconnectInProgress = sessionStorage.getItem(
            'disconnectInProgress',
        );
        if (disconnectInProgress === 'true') {
            return;
        }

        sessionStorage.setItem('disconnectInProgress', 'true');

        // Store the userAddress before clearing localStorage
        const userAddress = localStorage.getItem('userAddress');
        const usingThirdweb = localStorage.getItem('usingThirdweb');

        // Clear localStorage but preserve userAddress
        localStorage.clear();

        // Restore userAddress if it existed
        if (userAddress) {
            localStorage.setItem('userAddress', userAddress);
        }

        // Clean up thirdweb flag
        if (usingThirdweb) {
            localStorage.removeItem('usingThirdweb');
        }

        // Reset loading state
        this.setState({
            isLoadingBalance: false,
            accountBalance: 0,
            compactUserAddress: '',
        });

        this.props.clearNonce();
        this.props.resetUserSession();
        this.props.history.push('/');
        sessionStorage.removeItem('disconnectInProgress');
    };

    toggle = (index: number): void => {
        const collapse = `isOpen${index}` as keyof HeaderState;
        this.setState(
            (prevState) =>
            ({
                [collapse]: !prevState[collapse],
            } as unknown as Pick<HeaderState, keyof HeaderState>),
        );
    };

    handleScroll = (): void => {
        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
        const scrolled = scrollTop > 50;

        if (this.state.scrolled !== scrolled) {
            this.setState({ scrolled });
        }
    };

    render(): JSX.Element {
        const {
            web3Data,
            userDetails,
            accountBalance,
            compactUserAddress,
            isLoadingBalance,
            scrolled,
        } = this.state;
        const headerProfileImage =
            this.props.profileData?.profile ||
            this.props.authData?.data?.profile ||
            '';
        const value = this.props.location.pathname;

        // Helper function to check if path is active
        const isActive = (path: string) => {
            if (path === '/') return value === '/' || value === '/platform';
            if (path === '/marketplace')
                return (
                    value === '/marketplace' ||
                    value.startsWith('/primarymarketplace')
                );
            if (path === '/secondary')
                return (
                    value === '/secondary' ||
                    value.startsWith('/secondarymarketplace') ||
                    value.startsWith('/secondary-marketplace')
                );
            return value.startsWith(path);
        };

        const navLinks = [
            // { label: 'Platform', href: '/', id: 'platform' },
            {
                label: 'Primary',
                href: '/marketplace',
                id: 'nav_primary',
            },
            {
                label: 'Secondary',
                href: '/secondary',
                id: 'nav_secondary',
            },
            { label: 'Yield', href: '/rental', id: 'rental' },
            { label: 'How to use?', href: '/how-to-use', id: 'how_to_use?' },
            { label: 'About Us', href: '/about', id: 'about_us' },
        ];

        // Header animation variants - only animate on initial mount
        const headerVariants = {
            initial: {
                opacity: 1,
                y: 0,
            },
            animate: {
                opacity: 1,
                y: 0,
                transition: {
                    duration: 0,
                },
            },
        };

        // Navigation items stagger animation - disabled to prevent glitches on route changes
        const navItemVariants = {
            initial: {
                opacity: 1,
                y: 0,
            },
            animate: {
                opacity: 1,
                y: 0,
                transition: {
                    duration: 0,
                },
            },
        };

        return (
            <>
                <motion.header
                    initial='initial'
                    animate='animate'
                    variants={headerVariants}
                    className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 bg-background/95 backdrop-blur-[12px] border-b border-border ${scrolled ? 'shadow-lg' : ''
                        }`}
                    ref={this.wrapperRef}
                    {...({} as any)}
                >
                    <nav className='container mx-auto px-4 lg:px-8'>
                        <div className='flex items-center h-16 lg:h-20 w-full gap-2 lg:gap-4'>
                            {/* Logo */}
                            <Link
                                to='/'
                                className='flex items-center gap-3 group flex-shrink-0'
                                onClick={() => this.props.history.push('/')}
                            >
                                <VestnLogo size='md' />
                            </Link>

                            {/* Desktop Navigation - Center */}
                            <motion.div
                                className='desktop-nav-links hidden xl:flex items-center gap-0.5 lg:gap-1 flex-1 justify-center mx-4'
                                initial='initial'
                                animate='animate'
                                variants={{
                                    initial: {},
                                    animate: {
                                        transition: {
                                            staggerChildren: 0.05,
                                        },
                                    },
                                }}
                                {...({} as any)}
                            >
                                {navLinks.map((link, index) => (
                                    <motion.div
                                        key={link.href}
                                        variants={navItemVariants}
                                        {...({} as any)}
                                    >
                                        <NavLink
                                            to={link.href}
                                            exact
                                            activeClassName='active'
                                            className={`px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${isActive(link.href)
                                                ? 'text-primary bg-primary/10'
                                                : 'text-foreground hover:text-primary hover:bg-primary/10'
                                                }`}
                                        >
                                            <FormattedMessage
                                                id={link.id}
                                                defaultMessage={link.label}
                                            />
                                        </NavLink>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Right Section */}
                            <div className='flex items-center gap-1.5 lg:gap-2 xl:gap-3 flex-shrink-0 min-w-0 ml-auto'>
                                {/* Search Bar — opens the command palette (⌘K) */}
                                <button
                                    aria-label='Search assets and pages (Cmd+K)'
                                    onClick={() => {
                                        const event = new KeyboardEvent(
                                            'keydown',
                                            {
                                                key: 'k',
                                                metaKey: true,
                                            },
                                        );
                                        document.dispatchEvent(event);
                                    }}
                                    style={{ display: 'flex' }}
                                    className='items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-[#2F3A52] text-sm text-muted-foreground hover:text-foreground hover:bg-muted hover:border-[#3A4763] transition-colors min-h-9 flex-shrink-0'
                                >
                                    <Search className='w-4 h-4' />
                                    <span>
                                        Search...
                                    </span>
                                    <kbd className='ml-1 px-1.5 py-0.5 rounded bg-background/80 border border-border text-xs font-mono'>
                                        ⌘K
                                    </kbd>
                                </button>

                                {/* Command Palette */}
                                <div>
                                    <CommandPalette />
                                </div>

                                {/*
                                 * Light/Dark Mode Toggle (commented out)
                                 * <div>
                                 *     <ThemeToggle variant='button' />
                                 * </div>
                                 */}

                                {/* Notifications - Desktop - Using NotificationDropdown */}
                                {web3Data.isLoggedIn && (
                                    <div>
                                        <NotificationDropdown />
                                    </div>
                                )}

                                {/* Wallet Section - Desktop Only */}
                                {!web3Data.isLoggedIn ? (
                                    <div className='min-w-0 shrink-0 overflow-visible px-0.5 sm:max-w-none'>
                                        <DesktopLogin>
                                            <ThirdWebLogin
                                                toggle={() => this.toggle(4)}
                                                className='!h-10 !rounded-xl !bg-[#0B1324] !border !border-[#243047] hover:!border-[#31405F] !text-[#0B0E14] !shadow-none'
                                            />
                                        </DesktopLogin>
                                    </div>
                                ) : (
                                    <div
                                        className='flex items-center gap-2 xl:gap-3 flex-shrink-0 min-w-0'
                                        ref={this.wrapperRef}
                                    >
                                        <div className='flex-shrink-0 overflow-hidden'>
                                            {userDetails
                                                ? this.checkRole(userDetails)
                                                : ''}
                                        </div>

                                        {/* Account Dropdown */}
                                        <div className='relative flex-shrink-0'>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <button
                                                        className='wallet-dropdown-btn group flex w-[min(100vw-10rem,340px)] max-w-[min(100vw-10rem,340px)] min-w-0 items-center gap-2 !rounded-2xl border border-[#27344a] bg-[linear-gradient(90deg,#0A101D_0%,#101827_55%,#0B111F_100%)] py-2 pl-3 pr-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] outline-none transition-[border-color,box-shadow] hover:border-[#D4FF00] hover:shadow-[0_0_0_1px_rgba(212,255,0,0.25),0_0_20px_rgba(212,255,0,0.2),inset_0_1px_0_rgba(255,255,255,0.04)] focus-visible:ring-2 focus-visible:ring-[#D4FF00]/35 sm:w-auto sm:max-w-[min(100vw-2rem,340px)] sm:min-w-[220px] sm:gap-3 sm:pl-4 sm:pr-3'
                                                        style={{
                                                            maxWidth: '340px',
                                                            borderRadius: '16px',
                                                        }}
                                                        aria-label='Account menu'
                                                    >
                                                        {isLoadingBalance ? (
                                                            <>
                                                                <CircleLoader />
                                                                <span className='text-xs text-foreground'>
                                                                    Loading...
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className='flex items-center gap-2 min-w-0 flex-1'>
                                                                    <span className='max-w-[9.5rem] truncate text-xs font-bold tabular-nums leading-tight text-[#D4FF00] sm:max-w-none sm:text-sm'>
                                                                        {accountBalance.toLocaleString()}{' '}
                                                                        {
                                                                            nftDetails.nftSymbol
                                                                        }
                                                                    </span>
                                                                    <span
                                                                        className='h-4 w-px bg-[#2A3A56] shrink-0'
                                                                        aria-hidden
                                                                    />
                                                                    <span className='shrink-0 whitespace-nowrap font-mono text-[11px] font-medium tracking-wide text-[#8E9AAF] sm:text-xs'>
                                                                        {
                                                                            compactUserAddress
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <div className='w-8 h-8 rounded-full bg-gradient-vestn flex items-center justify-center shadow-glow-sm overflow-hidden'>
                                                                    {headerProfileImage ? (
                                                                        <img
                                                                            src={
                                                                                headerProfileImage
                                                                            }
                                                                            alt='Profile'
                                                                            className='w-full h-full object-cover'
                                                                        />
                                                                    ) : (
                                                                        <span className='text-xs font-bold text-primary-foreground'>
                                                                            V
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <ChevronDown className='w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors' />
                                                            </>
                                                        )}
                                                    </button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                    align='end'
                                                    className='!z-[12000] w-[min(calc(100vw-2rem),260px)] overflow-hidden !rounded-2xl border border-[#27344a] bg-[#070F22] p-1.5 text-[#E9EEF8] shadow-2xl shadow-black/40'
                                                >
                                                    <DropdownMenuItem className='!rounded-xl p-0 data-[highlighted]:!rounded-xl data-[highlighted]:!bg-[#D4FF00] data-[highlighted]:!text-[#0B0E14]' asChild>
                                                        <button
                                                            type='button'
                                                            onClick={() =>
                                                                this.props.history.push(
                                                                    '/user/profile',
                                                                )
                                                            }
                                                            className='flex w-full items-center gap-3 !rounded-xl px-3 py-3 text-left text-sm font-medium text-[#E9EEF8] transition-all duration-200'
                                                        >
                                                            <LayoutDashboard className='h-4 w-4 shrink-0' />
                                                            <ProfileLink>
                                                                <FormattedMessage
                                                                    id='dashboard'
                                                                    defaultMessage='Dashboard'
                                                                />
                                                            </ProfileLink>
                                                        </button>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem className='!rounded-xl p-0 data-[highlighted]:!rounded-xl data-[highlighted]:!bg-[#D4FF00] data-[highlighted]:!text-[#0B0E14]' asChild>
                                                        <button
                                                            type='button'
                                                            onClick={() => {
                                                                this.props.history.push(
                                                                    '/user/profile/transactions',
                                                                );
                                                                this.toggle(2);
                                                            }}
                                                            className='flex w-full items-center gap-3 !rounded-xl px-3 py-3 text-left text-sm font-medium text-[#E9EEF8] transition-all duration-200'
                                                        >
                                                            <HistoryIcon className='h-4 w-4 shrink-0' />
                                                            <ProfileLink>
                                                                <FormattedMessage
                                                                    id='transaction_history'
                                                                    defaultMessage='Transaction History'
                                                                />
                                                            </ProfileLink>
                                                        </button>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem className='!rounded-xl p-0 data-[highlighted]:!rounded-xl data-[highlighted]:!bg-[#D4FF00] data-[highlighted]:!text-[#0B0E14]' asChild>
                                                        <button
                                                            type='button'
                                                            onClick={() =>
                                                                this.props.history.push(
                                                                    '/user/profile/settings',
                                                                )
                                                            }
                                                            className='flex w-full items-center gap-3 !rounded-xl px-3 py-3 text-left text-sm font-medium text-[#E9EEF8] transition-all duration-200'
                                                        >
                                                            <Settings className='h-4 w-4 shrink-0' />
                                                            <ProfileLink>
                                                                <FormattedMessage
                                                                    id='settings'
                                                                    defaultMessage='Settings'
                                                                />
                                                            </ProfileLink>
                                                        </button>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator className='my-1.5 bg-[#2a3550]' />
                                                    <DropdownMenuItem
                                                        onClick={() => {
                                                            this.disconnect();
                                                        }}
                                                        className='flex w-full items-center gap-3 !rounded-xl px-3 py-3 text-left text-sm font-medium !text-[#FF4B4B] transition-all duration-200 data-[highlighted]:!rounded-xl data-[highlighted]:!bg-[#D4FF00] data-[highlighted]:!text-[#FF4B4B]'
                                                    >
                                                        <Wallet className='h-4 w-4 shrink-0 !text-[#FF4B4B]' />
                                                        <ProfileLink className='!text-[#FF4B4B]'>
                                                            <FormattedMessage
                                                                id='disconnect'
                                                                defaultMessage='Disconnect'
                                                            />
                                                        </ProfileLink>
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>
                                )}

                                {/* Mobile Menu Toggle */}
                                <button
                                    className={`xl:hidden p-2 rounded-xl border transition-all duration-200 ${this.state.isOpen11
                                        ? 'bg-primary/10 border-primary/30 text-white shadow-lg'
                                        : 'bg-card/70 border-border/60 hover:bg-card text-foreground hover:shadow-md'
                                        }`}
                                    onClick={() => this.toggle(11)}
                                    aria-label={
                                        this.state.isOpen11
                                            ? 'Close menu'
                                            : 'Open menu'
                                    }
                                    aria-expanded={this.state.isOpen11}
                                    aria-controls='mobile-navigation'
                                >
                                    {this.state.isOpen11 ? (
                                        <X className='w-6 h-6 text-white' />
                                    ) : (
                                        <Menu className='w-6 h-6 text-foreground' />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Mobile Sidebar Backdrop */}
                        {this.state.isOpen11 && (
                            <div
                                className='fixed inset-x-0 bottom-0 top-16 bg-black/60 backdrop-blur-md z-[9998] lg:hidden'
                                onClick={() => this.toggle(11)}
                                aria-hidden='true'
                            />
                        )}

                        {/* Mobile Navigation */}
                        <div id='mobile-navigation'>
                            <Collapse
                                isOpen={this.state.isOpen11}
                                className={
                                    'app__collapse xl:hidden fixed inset-x-0 top-16 z-[10000] ' +
                                    (this.state.isOpen11
                                        ? 'collapse-active'
                                        : '')
                                }
                            >
                                <div className='mx-3 mt-4 mb-4 overflow-y-auto overscroll-contain touch-pan-y max-h-[calc(100vh-5rem-1rem)] rounded-2xl border border-border/60 bg-background/90 backdrop-blur-xl shadow-2xl pt-4 pb-3'>
                                    <div className='flex flex-col'>
                                        {/* Section 1: User Actions (Wallet & Actions) */}
                                        {web3Data.isLoggedIn ? (
                                            <div className='px-4 mb-4 space-y-3'>
                                                {/* Wallet Address Button with Mobile Dropdown */}
                                                <div className='relative'>
                                                    <button
                                                        onClick={() => {
                                                            this.toggle(2); // Toggle wallet dropdown
                                                        }}
                                                        className='w-full flex items-center gap-3 px-4 py-3 transition-all duration-200 bg-card/70 border border-border/60 rounded-2xl outline-none cursor-pointer overflow-hidden hover:bg-card/90 shadow-sm hover:shadow-md'
                                                        style={{
                                                            borderRadius:
                                                                '12px',
                                                        }}
                                                    >
                                                        {isLoadingBalance ? (
                                                            <>
                                                                <CircleLoader />
                                                                <span className='text-xs text-foreground'>
                                                                    Loading...
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className='flex flex-col items-start flex-1 min-w-0'>
                                                                    <span className='text-sm font-bold tabular-nums leading-tight whitespace-nowrap text-foreground'>
                                                                        {accountBalance.toLocaleString()}{' '}
                                                                        {
                                                                            nftDetails.nftSymbol
                                                                        }
                                                                    </span>
                                                                    <span className='text-[10px] font-mono leading-tight mt-0.5 text-muted-foreground truncate max-w-full'>
                                                                        {
                                                                            compactUserAddress
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <ChevronDown
                                                                    className={`w-3.5 h-3.5 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${this
                                                                        .state
                                                                        .isOpen2
                                                                        ? 'rotate-180'
                                                                        : ''
                                                                        }`}
                                                                />
                                                            </>
                                                        )}
                                                    </button>

                                                    {/* Mobile Wallet Dropdown - Inline in hamburger menu */}
                                                    <Collapse
                                                        isOpen={
                                                            this.state.isOpen2
                                                        }
                                                        className={
                                                            'app__collapse collapse-css-transition mt-2 ' +
                                                            (this.state.isOpen2
                                                                ? 'collapse-active'
                                                                : '')
                                                        }
                                                    >
                                                        <div className='w-full rounded-2xl shadow-2xl overflow-hidden bg-background/95 backdrop-blur-xl border border-border/60'>
                                                            <DDBtnbar02>
                                                                {/* Dashboard */}
                                                                <button
                                                                    onClick={() => {
                                                                        this.props.history.push(
                                                                            '/user/profile',
                                                                        );
                                                                        this.toggle(
                                                                            2,
                                                                        );
                                                                        this.toggle(
                                                                            11,
                                                                        ); // Close hamburger menu
                                                                    }}
                                                                    className='w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left'
                                                                >
                                                                    <LayoutDashboard className='w-4 h-4 text-muted-foreground' />
                                                                    <ProfileLink>
                                                                        <FormattedMessage
                                                                            id='dashboard'
                                                                            defaultMessage='Dashboard'
                                                                        />
                                                                    </ProfileLink>
                                                                </button>

                                                                {/* Transaction History */}
                                                                <button
                                                                    onClick={() => {
                                                                        this.props.history.push(
                                                                            '/user/profile/transactions',
                                                                        );
                                                                        this.toggle(
                                                                            2,
                                                                        );
                                                                        this.toggle(
                                                                            11,
                                                                        ); // Close hamburger menu
                                                                    }}
                                                                    className='w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left'
                                                                >
                                                                    <Clock className='w-4 h-4 text-muted-foreground' />
                                                                    <ProfileLink>
                                                                        <FormattedMessage
                                                                            id='transaction_history'
                                                                            defaultMessage='Transaction History'
                                                                        />
                                                                    </ProfileLink>
                                                                </button>

                                                                {/* Settings */}
                                                                <button
                                                                    onClick={() => {
                                                                        this.props.history.push(
                                                                            '/user/profile/settings',
                                                                        );
                                                                        this.toggle(
                                                                            2,
                                                                        );
                                                                        this.toggle(
                                                                            11,
                                                                        ); // Close hamburger menu
                                                                    }}
                                                                    className='w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left'
                                                                >
                                                                    <Settings className='w-4 h-4 text-muted-foreground' />
                                                                    <ProfileLink>
                                                                        <FormattedMessage
                                                                            id='settings'
                                                                            defaultMessage='Settings'
                                                                        />
                                                                    </ProfileLink>
                                                                </button>

                                                                {/* Separator */}
                                                                <div className='border-t border-border my-1' />

                                                                {/* Disconnect */}
                                                                <button
                                                                    onClick={() => {
                                                                        this.disconnect();
                                                                        this.toggle(
                                                                            2,
                                                                        );
                                                                        this.toggle(
                                                                            11,
                                                                        );
                                                                    }}
                                                                    className='w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left text-destructive'
                                                                >
                                                                    <Wallet className='w-4 h-4 text-destructive' />
                                                                    <ProfileLink className='text-destructive'>
                                                                        <FormattedMessage
                                                                            id='disconnect'
                                                                            defaultMessage='Disconnect'
                                                                        />
                                                                    </ProfileLink>
                                                                </button>
                                                            </DDBtnbar02>
                                                        </div>
                                                    </Collapse>
                                                </div>

                                                {/* Become Investor / Create / Waitlist Button */}
                                                {userDetails && (
                                                    <div>
                                                        {userDetails.role
                                                            .roleName ===
                                                            'USER' ? (
                                                            <BecomeCreator />
                                                        ) : userDetails.role
                                                            .roleName ===
                                                            'CREATOR' &&
                                                            userDetails.status ===
                                                            'APPROVED' ? (
                                                            <Link
                                                                to='/user/nftminting'
                                                                onClick={() =>
                                                                    this.toggle(
                                                                        11,
                                                                    )
                                                                }
                                                            >
                                                                <AvBTN02 className='colorBTN w-full'>
                                                                    <FormattedMessage
                                                                        id='create'
                                                                        defaultMessage='Create'
                                                                    />
                                                                </AvBTN02>
                                                            </Link>
                                                        ) : userDetails.role
                                                            .roleName ===
                                                            'CREATOR' &&
                                                            userDetails.status !==
                                                            'APPROVED' ? (
                                                            <AvBTN02 className='grayBTN w-full'>
                                                                <FormattedMessage
                                                                    id='waitlist'
                                                                    defaultMessage='Waitlist'
                                                                />
                                                            </AvBTN02>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* Connect Wallet Button - Not Logged In */
                                            <div className='px-4 mb-4'>
                                                <div
                                                    onClick={() => {
                                                        this.toggle(4);
                                                        this.toggle(11); // Close hamburger menu
                                                    }}
                                                >
                                                    <ThirdWebLogin
                                                        toggle={() => {
                                                            this.toggle(4);
                                                            this.toggle(11);
                                                        }}
                                                        className='!h-11 !w-full !rounded-xl !bg-[#0B1324] !border !border-[#243047] hover:!border-[#31405F] !text-[#0B0E14] !shadow-none'
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Separator */}
                                        <div className='my-3 border-t border-border' />

                                        {/* Section 3: Navigation Links */}
                                        <div className='flex flex-col gap-1'>
                                            {navLinks.map((link) => (
                                                <NavLink
                                                    key={link.href}
                                                    to={link.href}
                                                    exact
                                                    activeClassName='active'
                                                    onClick={() =>
                                                        this.toggle(11)
                                                    }
                                                    className={`mx-2 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${isActive(link.href)
                                                        ? 'text-primary bg-primary/10 ring-1 ring-primary/20'
                                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                                                        }`}
                                                >
                                                    <FormattedMessage
                                                        id={link.id}
                                                        defaultMessage={
                                                            link.label
                                                        }
                                                    />
                                                </NavLink>
                                            ))}
                                        </div>

                                        {/* Section 4: User Account & Actions (if logged in) */}
                                        {web3Data.isLoggedIn && (
                                            <>
                                                <div className='my-4 border-t border-border/60' />
                                                <div className='flex flex-col gap-1'>
                                                    <NavLink
                                                        to='/user/profile'
                                                        exact
                                                        activeClassName='active'
                                                        onClick={() =>
                                                            this.toggle(11)
                                                        }
                                                        className={`mx-2 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${isActive(
                                                            '/user/profile',
                                                        )
                                                            ? 'text-primary bg-primary/10 ring-1 ring-primary/20'
                                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                                                            }`}
                                                    >
                                                        <FormattedMessage
                                                            id='profile'
                                                            defaultMessage='Profile'
                                                        />
                                                    </NavLink>

                                                    {/* Notifications */}
                                                    <div className='px-4 py-3'>
                                                        <NotificationDropdown />
                                                    </div>

                                                    {/* Disconnect */}
                                                    <button
                                                        onClick={() => {
                                                            this.disconnect();
                                                            this.toggle(11);
                                                        }}
                                                        className='mx-2 px-4 py-3.5 text-sm font-medium rounded-xl text-vestn-error hover:bg-muted/40 transition-colors text-left'
                                                    >
                                                        <FormattedMessage
                                                            id='disconnect'
                                                            defaultMessage='Disconnect'
                                                        />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Collapse>
                        </div>
                    </nav>
                </motion.header>
            </>
        );
    }
}

const mapDipatchToProps = (
    dispatch: ThunkDispatch<Web3State, unknown, AnyAction>,
) => {
    return {
        getWeb3: () => dispatch(actions.getWeb3()),
        enableMetamask: () => dispatch(actions.enableMetamask()),
        generateNonce: (address: string) =>
            dispatch(actions.generateNonce(address)),
        clearNonce: () => dispatch({ type: 'GENERATE_NONCE', data: null }),
        authLogin: (nonce: string, signature: string) =>
            dispatch(actions.authLogin(nonce, signature)),
        authenticateUser: () => dispatch(actions.authenticateUser()),
        getUserDetails: () => dispatch(actions.getUserDetails()),
        getReadNotifications: () => dispatch(actions.getreadNotifications()),
        getNotifications: () => {
            dispatch(actions.getNotifications());
        },
        authLogout: () => dispatch({ type: 'AUTH_LOGIN', data: null }),
        resetUserSession: () => dispatch(actions.resetUserSession()),
        resetUserSessionForWalletSwitch: (accounts: string[]) =>
            dispatch(actions.resetUserSessionForWalletSwitch(accounts)),
        web3Logout: (accounts: string[]) =>
            dispatch({
                type: 'FETCH_WEB3_DATA',
                data: { isLoggedIn: false, accounts: accounts },
            }),
    };
};

const mapStateToProps = (state: Web3State) => {
    return {
        web3Data: state.fetchWeb3Data,
        networkId: state.fetchNetworkId,
        isMetamaskEnabled: state.fetchMetamask,
        nonce: state.fetchNonce,
        authData: state.fetchAuthData,
        profileData: state.fetchProfile,
        notifications: state.fetchNotifications,
    };
};

export default withRouter(connect(mapStateToProps, mapDipatchToProps)(Header));
